import { create } from 'zustand';
import { api } from '../services/api';

export interface ListItem {
  id: string;
  list_id: string;
  name: string;
  category: string;
  quantity?: string;
  assigned_to?: string;
  assignee_name?: string;
  assignee_colour?: string;
  notes?: string;
  completed_at?: string | null;
  completed_by?: string;
  added_by: string;
  created_at: string;
  updated_at: string;
}

export interface ShoppingList {
  id: string;
  family_id: string;
  name: string;
  type: string;
  colour: string;
  created_by: string;
  created_at: string;
  pending_count?: number;
  total_count?: number;
}

export interface NutritionData {
  calories_kcal?: number | null;
  protein_g?: number | null;
  fat_g?: number | null;
  carbs_g?: number | null;
  fiber_g?: number | null;
  sugar_g?: number | null;
  sodium_mg?: number | null;
  vitamin_c_mg?: number | null;
}

interface ListsState {
  lists: ShoppingList[];
  currentList: ShoppingList | null;
  items: ListItem[];
  isLoading: boolean;
  error: string | null;
  nutritionCache: Record<string, NutritionData>;
  fetchLists: () => Promise<void>;
  fetchList: (listId: string) => Promise<void>;
  createList: (name: string, type?: string, colour?: string) => Promise<ShoppingList>;
  updateList: (listId: string, updates: { name?: string; colour?: string }) => Promise<ShoppingList>;
  deleteList: (listId: string) => Promise<void>;
  addItem: (listId: string, item: Partial<ListItem>) => Promise<void>;
  updateItem: (listId: string, itemId: string, updates: Partial<ListItem>) => Promise<void>;
  toggleItem: (listId: string, itemId: string) => Promise<void>;
  deleteItem: (listId: string, itemId: string) => Promise<void>;
  clearCompleted: (listId: string) => Promise<void>;
  fetchNutritionForItems: (itemNames: string[]) => Promise<void>;
  // WebSocket handlers
  handleWsMessage: (message: any) => void;
}

export const useListsStore = create<ListsState>((set, get) => ({
  lists: [],
  currentList: null,
  items: [],
  isLoading: false,
  error: null,
  nutritionCache: {},

  fetchLists: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get('/lists');
      set({ lists: res.data.lists, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  fetchList: async (listId) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get(`/lists/${listId}`);
      set({ currentList: res.data.list, items: res.data.items, isLoading: false });
    } catch (err: any) {
      set({ error: err.message, isLoading: false });
    }
  },

  createList: async (name, type = 'shopping', colour = '#c5512d') => {
    try {
      const res = await api.post('/lists', { name, type, colour });
      const newList = res.data.list;
      // Don't optimistically add — the WebSocket LIST_CREATED handler will add it.
      // Just ensure it's in state for immediate navigation (dedup-safe).
      set(state => {
        const exists = state.lists.find(l => l.id === newList.id);
        if (exists) return state;
        return { lists: [newList, ...state.lists] };
      });
      return newList;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateList: async (listId, updates) => {
    try {
      const res = await api.put(`/lists/${listId}`, updates);
      const updated = res.data.list;
      set(state => ({
        lists: state.lists.map(l => l.id === listId ? { ...l, ...updated } : l),
        currentList: state.currentList?.id === listId ? { ...state.currentList, ...updated } : state.currentList,
      }));
      return updated;
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  deleteList: async (listId) => {
    try {
      await api.delete(`/lists/${listId}`);
      set(state => ({
        lists: state.lists.filter(l => l.id !== listId),
        currentList: state.currentList?.id === listId ? null : state.currentList,
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  addItem: async (listId, item) => {
    try {
      const res = await api.post(`/lists/${listId}/items`, item);
      const newItem = res.data.item;
      // Dedup-safe add — WebSocket ITEM_ADDED may have already added it
      set(state => {
        const exists = state.items.find(i => i.id === newItem.id);
        if (exists) return state;
        return {
          items: [...state.items, newItem],
          lists: state.lists.map(l =>
            l.id === listId
              ? { ...l, pending_count: (l.pending_count || 0) + 1, total_count: (l.total_count || 0) + 1 }
              : l
          ),
        };
      });
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  updateItem: async (listId, itemId, updates) => {
    try {
      const res = await api.put(`/lists/${listId}/items/${itemId}`, updates);
      const updatedItem = res.data.item;
      set(state => ({
        items: state.items.map(i => i.id === itemId ? updatedItem : i),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  toggleItem: async (listId, itemId) => {
    const prev = get().items;
    // Optimistic: flip completed_at instantly so the check feels immediate.
    const now = new Date().toISOString();
    set(state => ({
      items: state.items.map(i =>
        i.id === itemId ? { ...i, completed_at: i.completed_at ? null : now } : i
      ),
    }));
    try {
      const res = await api.put(`/lists/${listId}/items/${itemId}/toggle`);
      const updatedItem = res.data.item;
      set(state => ({
        items: state.items.map(i => i.id === itemId ? updatedItem : i),
      }));
    } catch (err: any) {
      set({ items: prev, error: err.message });
      throw err;
    }
  },

  deleteItem: async (listId, itemId) => {
    const prev = get().items;
    set(state => ({ items: state.items.filter(i => i.id !== itemId) }));
    try {
      await api.delete(`/lists/${listId}/items/${itemId}`);
    } catch (err: any) {
      set({ items: prev, error: err.message });
      throw err;
    }
  },

  clearCompleted: async (listId) => {
    try {
      await api.post(`/lists/${listId}/clear-completed`);
      set(state => ({
        items: state.items.filter(i => !i.completed_at),
      }));
    } catch (err: any) {
      set({ error: err.message });
      throw err;
    }
  },

  fetchNutritionForItems: async (itemNames) => {
    const { nutritionCache } = get();
    // Only fetch names not already cached
    const uncached = itemNames.filter(n => !nutritionCache[n.toLowerCase()]);
    if (uncached.length === 0) return;

    try {
      const res = await api.post('/nutrition/by-names', { names: uncached });
      const foods = res.data.foods || [];
      const newCache: Record<string, NutritionData> = {};
      for (const food of foods) {
        newCache[food.name.toLowerCase()] = {
          calories_kcal: food.calories_kcal ? Number(food.calories_kcal) : null,
          protein_g: food.protein_g ? Number(food.protein_g) : null,
          fat_g: food.fat_g ? Number(food.fat_g) : null,
          carbs_g: food.carbs_g ? Number(food.carbs_g) : null,
          fiber_g: food.fiber_g ? Number(food.fiber_g) : null,
          sugar_g: food.sugar_g ? Number(food.sugar_g) : null,
          sodium_mg: food.sodium_mg ? Number(food.sodium_mg) : null,
          vitamin_c_mg: food.vitamin_c_mg ? Number(food.vitamin_c_mg) : null,
        };
      }
      set(state => ({ nutritionCache: { ...state.nutritionCache, ...newCache } }));
    } catch {
      // Silently fail — nutrition is non-critical
    }
  },

  handleWsMessage: (message) => {
    const { currentList, lists } = get();

    switch (message.type) {
      case 'LIST_CREATED':
        set(state => {
          const exists = state.lists.find(l => l.id === message.payload.id);
          if (exists) return state;
          return { lists: [message.payload, ...state.lists] };
        });
        break;

      case 'LIST_UPDATED':
        set(state => ({
          lists: state.lists.map(l => l.id === message.payload.id ? { ...l, ...message.payload } : l),
        }));
        break;

      case 'LIST_DELETED':
        set(state => ({
          lists: state.lists.filter(l => l.id !== message.payload.listId),
        }));
        break;

      case 'ITEM_ADDED':
        set(state => {
          const newItems = currentList?.id === message.payload.listId
            ? (state.items.find(i => i.id === message.payload.item.id) ? state.items : [...state.items, message.payload.item])
            : state.items;
          return {
            items: newItems,
            lists: state.lists.map(l =>
              l.id === message.payload.listId && !state.items.find(i => i.id === message.payload.item.id)
                ? { ...l, pending_count: (l.pending_count || 0) + 1, total_count: (l.total_count || 0) + 1 }
                : l
            ),
          };
        });
        break;

      case 'ITEM_UPDATED':
      case 'ITEM_TOGGLED':
        if (currentList?.id === message.payload.listId) {
          set(state => ({
            items: state.items.map(i =>
              i.id === message.payload.item.id ? message.payload.item : i
            ),
          }));
        }
        break;

      case 'ITEM_DELETED':
        if (currentList?.id === message.payload.listId) {
          set(state => ({
            items: state.items.filter(i => i.id !== message.payload.itemId),
          }));
        }
        break;

      case 'LIST_ITEMS_BATCH_ADDED':
        set(state => {
          if (currentList?.id !== message.payload.listId) return state;
          const existingIds = new Set(state.items.map(i => i.id));
          const newItems = (message.payload.items || []).filter((i: any) => !existingIds.has(i.id));
          return {
            items: [...state.items, ...newItems],
            lists: state.lists.map(l =>
              l.id === message.payload.listId
                ? { ...l, pending_count: (l.pending_count || 0) + newItems.length, total_count: (l.total_count || 0) + newItems.length }
                : l
            ),
          };
        });
        break;

      case 'COMPLETED_CLEARED':
        if (currentList?.id === message.payload.listId) {
          set(state => ({
            items: state.items.filter(i => !i.completed_at),
          }));
        }
        break;
    }
  },
}));
